import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import styles from './Anchors.module.scss';

/**
 * Navigation to different Anchors on a page
 */
export default function Anchors({
  items, sticky, onSetActive, onItemClick,
}) {
  const [active, setActive] = useState(null);

  useEffect(() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync('html { scroll-behavior: smooth; }');
    document.adoptedStyleSheets.push(sheet);

    const observerCallback = (entries) => (
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const newActive = items.findIndex((item) => (item.href === `#${entry.target.id}`));
          setActive(newActive);
          onSetActive(items[newActive].name);
        }
      })
    );

    const elms = [];

    const observer = new IntersectionObserver(observerCallback, {
      root: null, // default is the viewport
      threshold: 1, // percentage of target's visible area. Triggers "onIntersection"
    });

    items.forEach((item) => {
      const elm = document.querySelector(item.href);
      if (elm !== null) {
        elms.push(elm);
        observer.observe(elm);
      }
    });

    return () => {
      document.adoptedStyleSheets = document.adoptedStyleSheets.filter((item) => item !== sheet);
      elms.forEach((elm) => {
        observer.unobserve(elm);
      });
    };
  }, []);

  const containerClassName = [
    styles.anchors,
    sticky ? styles['--sticky'] : '',
  ].join(' ').trim();

  const itemClassName = (index) => {
    const className = [
      styles.anchors__item,
      index === active ? styles['--active'] : '',
      items[index].hideOnMobile ? styles['--hide-on-mobile'] : '',
    ].join(' ').trim();
    return className;
  };

  return (
    <nav className={containerClassName}>
      <ul className={styles.anchors__items}>
        {items && items.map((item, index) => (
          <li className={itemClassName(index)} key={index}><a className={styles['anchors__item-link']} href={item.href} onClick={onItemClick}>{item.name}</a></li>
        ))}
      </ul>
    </nav>
  );
}

Anchors.defaultProps = {
  sticky: true,
  onSetActive: () => {},
  onItemClick: () => {},
};

Anchors.propTypes = {
  /**
   * Navigation Items
   */
  // eslint-disable-next-line react/forbid-prop-types
  items: PropTypes.array.isRequired,
  /**
   * Sticky on scroll
   */
  sticky: PropTypes.bool,
  /**
   * function to call when setting active anchor
   */
  onSetActive: PropTypes.func,
  /**
   * function to call when item is clicked in the menu
   */
  onItemClick: PropTypes.func,
};
